import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { map, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { HotToastService } from '@ngxpert/hot-toast';

import { environment } from '@environments/environment';
import { CarService } from './car.service';
import { GlobalsService } from '@services/globals.service';
import { FinancingFormDataResponse, FinancingFormDataProduct, FinancingFormContactInformations } from '@models/financing-form.model';

@Injectable({
  providedIn: 'root',
})
export class FinancingFormService {
  private http = inject(HttpClient);
  private carService = inject(CarService);
  private globalsService = inject(GlobalsService);
  private hotToastService = inject(HotToastService);
  private translateService = inject(TranslateService);
  public formId: WritableSignal<string | null> = signal(null);
  public financingFormData: WritableSignal<FinancingFormDataResponse | undefined> = signal(undefined);
  public step: WritableSignal<number> = signal(1);
  public totalSteps: WritableSignal<number> = signal(3);
  public product: WritableSignal<FinancingFormDataProduct | undefined> = signal(undefined);
  public contactInfoData: WritableSignal<FinancingFormContactInformations | undefined> = signal(undefined);
  public carContractData: WritableSignal<{ carId?: number; numberOfMonths?: number; advance?: number; loanType?: string } | undefined> = signal(undefined);

  resetData(): void {
    this.product.set(undefined);
    this.contactInfoData.set(undefined);
    this.carContractData.set(undefined);
    this.formId.set(null);
    this.globalsService.headerHashLogged.set({});
    this.globalsService.headerHashMD5.set({});
    this.globalsService.token.set('');
  }

  getFinancingFormData(carId: number, numberOfMonths: number, advance: number, loanType: number): Observable<FinancingFormDataResponse | undefined> {
    const data = this.financingFormData();
    if (data) {
      if (data.carId === carId && data.numberOfMonths === numberOfMonths && data.advance === advance && data.loanType === loanType) return of(data);
      if (data.carId !== carId) this.resetData();
      data.carId = carId;
    }

    const largeProductLogo = [124, 125, 126];

    return this.http.post<FinancingFormDataResponse>(`${environment.productsApi}`, { carId, numberOfMonths, advance, loanType }, { headers: this.globalsService.headerHash() }).pipe(
      this.hotToastService.observe({
        error: this.translateService.instant('forms.hotToast.products.error'),
      }),
      map((response: FinancingFormDataResponse) => {
        let foundMonths = false;
        let foundAdvance = false;
        response.message.car.leasingOptions.forEach((item) => {
          item.paymentPlans.forEach((plan: any) => {
            if (plan.numberOfMonths === numberOfMonths) {
              foundMonths = true;
              if (plan.advancePrice === advance) {
                foundAdvance = true;
              }
            }
          });
          if (foundAdvance) return;
        });
        if (!foundMonths) numberOfMonths = 120;
        if (!foundAdvance) advance = (response.message.car.price / 100) * 70;
        this.financingFormData.set(response);
        response.loanType = loanType;
        response.carId = carId;
        response.numberOfMonths = numberOfMonths;
        response.advance = advance;
        response.message.car.leasingOptions = this.carService.calculatePaymentOptions(response.message.car.leasingOptions, response.message.car.price);
        response.message.leasingProducts.forEach((product: FinancingFormDataProduct) => {
          product.logoSize = largeProductLogo.includes(product.id) ? 'large' : 'default';
          product.interestRatePercentage = parseFloat((product.interestRate * 100).toFixed(3));
          product.aprPercentage = parseFloat((product.apr * 100).toFixed(3));
          const advance = this.financingFormData()!.advance;
          const numberOfMonths = this.financingFormData()!.numberOfMonths;
          const leasingOptions = this.financingFormData()!.message.car.leasingOptions;
          const matchingOption = leasingOptions.find(option =>
            option.paymentPlans.some((plan: any) =>
              plan.advancePrice === advance && plan.numberOfMonths === numberOfMonths
            )
          );
          const advancePercentage = matchingOption ? matchingOption.advance : null;
          product.advancePercentage = advancePercentage;
        });
        return response;
      }),
    );
  }
}
